import { Component, computed, input } from '@angular/core';
import { DatePipe } from '@angular/common';
import { StateBackgroundCheck } from '@umss/shared/data-access';
import { UmssCardHeaderComponent } from '../card/card-header/card-header.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import { UmssVerifiedIndicatorComponent } from '../verified-indicator/verified-indicator.component';

/**
 * Read-only presentation of a contractor's manually verified state background
 * check, shared by the facility and contractor apps so the two stay identical.
 *
 * Presentational only — the parent fetches and supplies `stateCheck`.
 *
 * Shows the verification, its date and (for a facility) the administrator's
 * note — never the uploaded document itself. A facility sees only the
 * attestation; the contractor manages their own upload through the list
 * projected into `[card-actions]`.
 *
 * The business app is deliberately NOT a third audience here: its version is an
 * editable form with a save action, which is a different component rather than a
 * mode of this one. Consistency between all three surfaces comes from the shared
 * pieces they all use — the header icon, UmssVerifiedIndicatorComponent, and the
 * document thumbnail — not from one component with three layouts.
 */
@Component({
  selector: 'umss-state-background-check-card',
  standalone: true,
  imports: [
    DatePipe,
    UmssCardHeaderComponent,
    UmssSkeletonComponent,
    UmssVerifiedIndicatorComponent
  ],
  templateUrl: './state-background-check-card.component.html',
  styleUrl: './state-background-check-card.component.scss'
})
export class UmssStateBackgroundCheckCardComponent {
  stateCheck = input<StateBackgroundCheck | null | undefined>(null);

  loading = input(false);

  /**
   * Who is looking. `facility` additionally renders the administrator's note;
   * `contractor` never does, since that text is admin-authored commentary about
   * the professional rather than something written for them.
   */
  audience = input<'facility' | 'contractor'>('facility');

  /**
   * Standing explanatory copy. Overrides the audience default below.
   *
   * The defaults are product-supplied copy — expected to be revised later, so
   * keep them in one place here rather than duplicating them per app.
   */
  description = input<string | null>(null);

  /**
   * Default copy per audience, because the two need different things from it: a
   * facility is reading a result and needs to know what the credential means,
   * while a contractor is looking at an action they may still have to take.
   */
  protected readonly descriptionText = computed(
    () =>
      this.description() ??
      (this.audience() === 'facility'
        ? "Use this information to confirm this professional's state-required background check."
        : 'Any facility you work with will need to verify your state background check. If your state issues you a card, please upload an image of that here. Huro will verify your background check internally and mark you as verified.')
  );

  /**
   * Label for the administrator's note. Names the author: without it a facility
   * cannot tell Huro's statement apart from something the professional wrote
   * about themselves.
   */
  protected readonly notesLabel = 'Note from Huro';

  protected readonly isVerified = computed(
    () => !!this.stateCheck()?.isVerified
  );

  protected readonly verifiedDate = computed(
    () => this.stateCheck()?.verifiedDate ?? null
  );

  /** Admin note — facility audience only, and only when non-empty. */
  protected readonly notes = computed(() => {
    if (this.audience() !== 'facility') return null;
    return this.stateCheck()?.notes?.trim() || null;
  });
}
